// Sidebar (design-system §3.1, §3.2): Betrieb, Standort, Suche, Navigation aus den Manifesten, unten
// Einstellungen und Benutzer. Liegt auf `canvas`; der aktive Eintrag ist eine weiße Fläche mit Haarlinie.
// 248 px breit, eingeklappt eine 56-px-Schiene mit Symbolen (⌘B). Auf dem Telefon steht derselbe Baum im Sheet „Mehr“.
import { ChevronDown, ChevronRight, ChevronUp, Search, Settings2 } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router'
import { useActionQuery } from '../api/hooks'
import { cn } from '../lib/cn'
import type { NavBadge } from '../module'
import { shell, useShellStore } from '../stores/shell'
import { Kbd } from '../ui/Kbd'
import { Tooltip } from '../ui/Tooltip'
import { DemoMark } from './DemoMark'
import { activeNavItem, type NavGroup, type NavItem, type Navigation } from './nav'
import { RouterLink } from './RouterLink'
import { LocationSwitcher, TenantSwitcher } from './Switchers'
import { UserMenu } from './UserMenu'

const row =
  'flex h-8 w-full min-w-0 items-center gap-2 rounded-sm px-2 text-dense transition-colors duration-(--duration-instant) touch:h-11 touch:text-touch'
const idle = 'text-fg-muted hover:bg-nav-hover hover:text-fg'
const current = 'border border-line bg-nav-active font-medium text-fg shadow-xs'

const BADGE_TONE: Record<NonNullable<NavBadge['tone']>, string> = {
  neutral: 'text-fg-subtle',
  accent: 'text-fg-accent',
  warning: 'text-warning-fg',
  danger: 'text-danger-fg',
}

/** Zähler eines Eintrags: nur Dinge, die eine Handlung erfordern. Fehler und 0 zeigen nichts. */
function NavCount({ badge, dot }: { badge: NavBadge; dot?: boolean }) {
  const query = useActionQuery(badge.query, badge.input ?? {}, {
    select: (data) => {
      const value = data as { count?: unknown; total?: unknown }
      return typeof value.count === 'number' ? value.count : typeof value.total === 'number' ? value.total : 0
    },
    staleTime: 30_000,
    refetchInterval: 120_000,
    refreshOn: '*',
  })
  const count = query.data ?? 0
  if (count <= 0) return null
  if (dot) return <span className="absolute top-1.5 right-1.5 size-1.5 rounded-full bg-accent" aria-hidden />
  return (
    <span
      className={cn('text-caption tabular-nums', BADGE_TONE[badge.tone ?? 'neutral'])}
      aria-label={`${count} offen`}
    >
      {count > 999 ? '999+' : count}
    </span>
  )
}

function NavRow({
  item,
  active,
  collapsed,
  onNavigate,
}: {
  item: NavItem
  active: boolean
  collapsed: boolean
  onNavigate?: () => void
}) {
  const link = (
    <RouterLink
      href={item.to}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
      onClick={onNavigate}
      className={cn(row, 'relative', active ? current : idle, collapsed && 'justify-center px-0')}
    >
      <item.icon className="size-4 shrink-0 touch:size-5" strokeWidth={1.75} aria-hidden />
      {!collapsed && <span className="min-w-0 flex-1 truncate-line">{item.label}</span>}
      {item.badge && <NavCount badge={item.badge} dot={collapsed} />}
    </RouterLink>
  )
  if (!collapsed) return link
  return (
    <Tooltip content={item.label} side="right">
      {link}
    </Tooltip>
  )
}

function Group({
  group,
  activeKey,
  collapsed,
  onNavigate,
  forcedOpen = false,
  onRelease,
  defaultFolded = false,
  icon: Icon,
}: {
  group: NavGroup
  activeKey: string | null
  collapsed: boolean
  onNavigate?: () => void
  /** die Gruppe der aktuellen Seite: nach dem Wechsel dorthin offen, auch wenn die Person sie eingeklappt hat */
  forcedOpen?: boolean
  /** die Person klappt die von selbst geöffnete Gruppe wieder ein */
  onRelease?: () => void
  /** Standard eingeklappt (Einstellungen): gemerkt wird dann das AUFklappen (`<key>:open`) */
  defaultFolded?: boolean
  icon?: NavItem['icon']
}) {
  const storeKey = defaultFolded ? `${group.key}:open` : group.key
  const marked = useShellStore((state) => state.collapsedSections.includes(storeKey))
  const preferFolded = defaultFolded ? !marked : marked
  const folded = preferFolded && !forcedOpen
  if (collapsed) {
    return (
      <div className="flex flex-col gap-0.5 border-t border-line pt-2" role="group" aria-label={group.label}>
        {group.items.map((item) => (
          <NavRow key={item.key} item={item} active={item.key === activeKey} collapsed />
        ))}
      </div>
    )
  }
  const toggle = (): void => {
    // von selbst geöffnet und eigentlich eingeklappt: nur wieder zuklappen – die Vorliebe bleibt
    if (forcedOpen) onRelease?.()
    if (!(forcedOpen && preferFolded)) shell.toggleSection(storeKey)
  }
  // eine eingeklappte Gruppe zeigt weiter den Eintrag, auf dem man steht
  const visible = folded ? group.items.filter((item) => item.key === activeKey) : group.items
  return (
    <div className="flex flex-col gap-0.5" role="group" aria-label={group.label} data-section={group.key}>
      <button
        type="button"
        aria-expanded={!folded}
        onClick={toggle}
        className={cn(
          'group/section flex w-full items-center gap-1.5 rounded-xs px-2 pb-1.5 text-left text-micro text-fg-muted uppercase hover:text-fg touch:min-h-11 touch:pb-0',
          Icon ? 'pt-1.5' : 'pt-4 touch:pt-2',
        )}
      >
        {Icon && <Icon className="size-4 shrink-0 text-fg-subtle touch:size-5" strokeWidth={1.75} aria-hidden />}
        <span className="flex-1">{group.label}</span>
        {folded && <span className="text-caption text-fg-subtle tabular-nums normal-case">{group.items.length}</span>}
        {/* immer sichtbar: ohne Zeiger (Telefon) gäbe es sonst keinen Hinweis, dass die Gruppe einklappt */}
        <ChevronRight
          className={cn('size-3.5 text-fg-subtle transition-[rotate] duration-(--duration-fast) touch:size-4', !folded && 'rotate-90')}
          strokeWidth={1.75}
          aria-hidden
        />
      </button>
      {visible.map((item) => (
        <NavRow key={item.key} item={item} active={item.key === activeKey} collapsed={false} onNavigate={onNavigate} />
      ))}
    </div>
  )
}

/** Die Gruppe der aktuellen Seite ist nach jedem Seitenwechsel offen – bis die Person sie selbst einklappt. */
function useOpenedSection(navigation: Navigation, activeKey: string | null): [string | null, () => void] {
  const section = navigation.all.find((item) => item.key === activeKey)?.section ?? null
  const [opened, setOpened] = useState<string | null>(section)
  useEffect(() => setOpened(section), [section, activeKey])
  return [opened, useCallback(() => setOpened(null), [])]
}

/**
 * Rollbereich der Navigation: zeigt oben und unten, dass es weitergeht (Verlauf + Pfeil),
 * und holt den Eintrag der aktuellen Seite ins Bild. Ohne Rollbalken wüsste sonst niemand von den Gruppen dahinter.
 */
export function NavScroll({ children, watch, className }: { children: ReactNode; watch: unknown; className?: string }) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [edges, setEdges] = useState({ above: false, below: false })
  const measure = useCallback((): void => {
    const node = ref.current
    if (!node) return
    const above = node.scrollTop > 4
    const below = node.scrollHeight - node.clientHeight - node.scrollTop > 4
    setEdges((current) => (current.above === above && current.below === below ? current : { above, below }))
  }, [])
  // die aktuelle Seite ist zu sehen – auch wenn ihre Gruppe weit unten steht
  useLayoutEffect(() => {
    const node = ref.current
    const active = node?.querySelector<HTMLElement>('[aria-current="page"]')
    if (node && active) {
      const top = active.offsetTop - node.offsetTop
      if (top < node.scrollTop + 28) node.scrollTop = Math.max(0, top - 56)
      else if (top + active.offsetHeight > node.scrollTop + node.clientHeight - 28) node.scrollTop = top + active.offsetHeight - node.clientHeight + 56
    }
    measure()
  }, [watch, measure])
  useEffect(() => {
    const node = ref.current
    if (!node || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    for (const child of node.children) observer.observe(child)
    return () => observer.disconnect()
  }, [measure])
  // rein optisch (pointer-events-none): Der Hinweis liegt über der Liste und darf keinen Klick auf einen Eintrag abfangen
  const hint = 'pointer-events-none absolute inset-x-0 z-10 flex h-8 justify-center from-canvas via-canvas/80 to-transparent text-fg-muted'
  return (
    <div className={cn('relative flex min-h-0 flex-1 flex-col', className)} data-more-above={edges.above || undefined} data-more-below={edges.below || undefined}>
      {edges.above && (
        <div className={cn(hint, 'top-0 items-start bg-linear-to-b')} data-testid="nav-more-above" aria-hidden>
          <ChevronUp className="size-4" strokeWidth={1.75} />
        </div>
      )}
      <div ref={ref} onScroll={measure} data-testid="nav-scroll" className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto scrollbar-none">
        {children}
      </div>
      {edges.below && (
        <div className={cn(hint, 'bottom-0 items-end bg-linear-to-t')} data-testid="nav-more-below" aria-hidden>
          <ChevronDown className="size-4" strokeWidth={1.75} />
        </div>
      )}
    </div>
  )
}

export interface NavTreeProps {
  navigation: Navigation
  collapsed?: boolean
  /** nach einem Klick auf einen Eintrag (Sheet schließen) */
  onNavigate?: () => void
}

/** Der Navigationsbaum allein – in der Sidebar und im Sheet „Mehr“ derselbe. */
export function NavTree({ navigation, collapsed = false, onNavigate }: NavTreeProps) {
  const { pathname } = useLocation()
  const activeKey = activeNavItem(navigation.all, pathname)?.key ?? null
  const [opened, release] = useOpenedSection(navigation, activeKey)
  return (
    <>
      <div className="flex flex-col gap-0.5">
        {navigation.top.map((item) => (
          <NavRow
            key={item.key}
            item={item}
            active={item.key === activeKey}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        ))}
      </div>
      {navigation.groups.map((group) => (
        <Group
          key={group.key}
          group={group}
          activeKey={activeKey}
          collapsed={collapsed}
          onNavigate={onNavigate}
          forcedOpen={opened === group.key}
          onRelease={release}
        />
      ))}
    </>
  )
}

export interface SidebarProps {
  navigation: Navigation
  /** 56-px-Schiene */
  collapsed: boolean
  onShowShortcuts?: () => void
}

export function Sidebar({ navigation, collapsed, onShowShortcuts }: SidebarProps) {
  const { pathname } = useLocation()
  const activeKey = activeNavItem(navigation.all, pathname)?.key ?? null
  const [opened, release] = useOpenedSection(navigation, activeKey)
  const folds = useShellStore((state) => state.collapsedSections)
  const searchButton = (
    <button
      type="button"
      onClick={() => shell.openPalette()}
      aria-label="Suchen und Befehle"
      data-testid="open-palette"
      className={cn(row, idle, collapsed && 'justify-center px-0')}
    >
      <Search className="size-4 shrink-0" strokeWidth={1.75} aria-hidden />
      {!collapsed && (
        <>
          <span className="flex-1 text-left">Suchen …</span>
          <Kbd hotkey="mod+k" className="touch:hidden" />
        </>
      )}
    </button>
  )
  return (
    <nav
      aria-label="Hauptnavigation"
      data-testid="sidebar"
      data-collapsed={collapsed || undefined}
      className={cn(
        'flex shrink-0 flex-col gap-0.5 overflow-hidden pt-2 pb-3 transition-[width] duration-(--duration-base) ease-out motion-reduce:transition-none',
        collapsed ? 'w-(--sidebar-w-rail) px-2' : 'w-(--sidebar-w) px-2',
      )}
      data-print="hide"
    >
      <TenantSwitcher collapsed={collapsed} />
      {/* Demo-Betrieb: die Marke steht direkt unter dem Namen, bündig mit ihm – der Name behält die ganze Zeile
          (in der Schiene nennt sie der Tooltip des Betriebs) */}
      {!collapsed && <DemoMark className="-mt-1.5 mb-1.5 ml-9 self-start" />}
      <LocationSwitcher collapsed={collapsed} />
      {collapsed ? (
        <Tooltip content="Suchen und Befehle" hotkey="mod+k" side="right">
          {searchButton}
        </Tooltip>
      ) : (
        searchButton
      )}
      <NavScroll className="mt-2" watch={`${pathname}|${collapsed}|${folds.join(',')}|${navigation.all.length}`}>
        <NavTree navigation={navigation} collapsed={collapsed} />
      </NavScroll>
      {/* Einstellungen: eine Zeile, die aufklappt – mit jedem Modul kämen sonst weitere feste Zeilen dazu */}
      <div className="flex flex-col gap-0.5 border-t border-line-subtle pt-1.5">
        {navigation.settings.length > 0 && (
          <div className={cn('flex flex-col gap-0.5', !collapsed && 'max-h-[40vh] overflow-y-auto scrollbar-none')} data-testid="nav-settings">
            <Group
              group={{ key: 'settings', label: 'Einstellungen', items: navigation.settings }}
              activeKey={activeKey}
              collapsed={collapsed}
              forcedOpen={opened === 'settings'}
              onRelease={release}
              defaultFolded
              icon={Settings2}
            />
          </div>
        )}
        <UserMenu
          variant={collapsed ? 'avatar' : 'row'}
          side={collapsed ? 'right' : 'top'}
          onShowShortcuts={onShowShortcuts}
        />
      </div>
    </nav>
  )
}
